/**
 * Read a JSON API response — or say, in words, why it was not one.
 *
 * Every form on the site does `await res.json()` on whatever comes back, and
 * whatever comes back is not always ours. Cloudflare sits in front of the
 * origin, and its WAF answers a request it dislikes with its own HTML
 * ("Just a moment…", cf-mitigated: challenge) that never reaches Railway.
 * Real artwork — a photo, a PDF, anything high-entropy — trips it, so the
 * customer saw `SyntaxError: Unexpected token '<', "<!DOCTYPE "...` where an
 * error message should have been.
 */
export class NotJsonError extends Error {
  constructor(
    message: string,
    /** True when Cloudflare, not our server, produced the response. */
    public readonly challenged: boolean,
    public readonly status: number,
  ) {
    super(message);
    this.name = "NotJsonError";
  }
}

export const CHALLENGE_MESSAGE =
  "Our security layer blocked this upload before it reached us. Please try again in a " +
  "moment; if it keeps happening, email the file to online@areaadvertising.com and we " +
  "will attach it for you.";

export async function readJson<T = Record<string, unknown>>(res: Response): Promise<T> {
  const type = res.headers.get("content-type") ?? "";
  const challenged = res.headers.get("cf-mitigated") === "challenge";
  if (challenged || !/json/i.test(type)) {
    // Peek at the body only to be sure; a JSON body with a missing header
    // is still JSON.
    const text = await res.text().catch(() => "");
    if (!challenged) {
      try {
        return JSON.parse(text) as T;
      } catch {
        /* fall through */
      }
    }
    throw new NotJsonError(
      challenged || /^\s*<!doctype html/i.test(text)
        ? CHALLENGE_MESSAGE
        : `Unexpected response from the server (HTTP ${res.status}). Please try again.`,
      challenged || /^\s*<!doctype html/i.test(text),
      res.status,
    );
  }
  return (await res.json()) as T;
}
